import { InputVariantTypes, PAYMENT_TYPE } from "@ecologi/shared";
import styled from "@emotion/styled";
import React, { useState } from "react";

import {
  CardDetailsDisplay,
  BillingAddressDisplay,
  EditDataCard,
  BillingDetailsForm,
  BillingPaymentSetup,
  FormFooter,
  PaymentOptionSelection,
  Typography,
} from "@ecologi/react-components";
import { useStore } from "../../../store";
import { useAddCardDetails, useUpdateProfile } from "../../profile/hooks";

const Container = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const StyledEditCard = styled(EditDataCard)`
  flex: 1 0 300px;
  height: fit-content;
  min-height: 224px;
`;

type Props = {
  poNumber: string;
  setPoNumber: (poNumber: string) => void;
  taxCode: string;
  setTaxCode: (taxCode: string) => void;
  paymentType: PAYMENT_TYPE;
  setPaymentType: (paymentType: PAYMENT_TYPE) => void;
};

export const BillingDetailsManagement = ({
  paymentType,
  setPaymentType,
  poNumber,
  setPoNumber,
  taxCode,
  setTaxCode,
}: Props) => {
  const { user, stripe } = useStore();
  const handleAddressUpdate = useUpdateProfile("Billing details updated");
  const addCardDetails = useAddCardDetails();
  const [isUserEditingAddress, setIsUserEditingAddress] = useState(false);
  const [isUserEditingPayment, setIsUserEditingPayment] = useState(false);

  const shouldDisplayAddressEdit = isUserEditingAddress || !user.hasAddress();

  const shouldDisplayPaymentEdit =
    isUserEditingPayment ||
    (!user.hasCard() && paymentType === PAYMENT_TYPE.CARD);

  return (
    <>
      <Container>
        <StyledEditCard
          displayTitle="Billing details"
          showEditToggle={user.hasAddress()}
          isEditing={shouldDisplayAddressEdit}
          onOpenEdit={() => setIsUserEditingAddress(!isUserEditingAddress)}
          displayContent={
            <BillingAddressDisplay
              billingEmail={user.billingEmail}
              billingName={
                user.billingCompanyName || `${user.firstName} ${user.lastName}`
              }
              firstLine={user.billingAddress1}
            />
          }
          editTitle={
            user.hasAddress()
              ? "Update billing details"
              : "Enter billing details"
          }
          editContent={
            <BillingDetailsForm
              user={user}
              onSubmitBillingDetails={handleAddressUpdate}
              onSuccessfulSubmit={() => {
                window.scrollTo(0, 0);
                setIsUserEditingAddress(false);
              }}
              inputVariant={InputVariantTypes.SNOWDROP}
              buttonPlacement="right"
              onCancel={() => {
                window.scrollTo(0, 0);
                setIsUserEditingAddress(false);
              }}
              showCancel={user.hasAddress()}
            />
          }
          displayLowerContent={"These details appear on your purchase invoice"}
        />
        <StyledEditCard
          displayTitle="Payment details"
          showEditToggle={user.hasCard() && paymentType === PAYMENT_TYPE.CARD}
          isEditing={shouldDisplayPaymentEdit}
          onOpenEdit={() => setIsUserEditingPayment(!isUserEditingPayment)}
          showBothContentsWhileEditing={user.isPBIEligible()}
          displayContent={
            user.isPBIEligible() ? (
              <PaymentOptionSelection
                paymentType={paymentType}
                setPaymentType={setPaymentType}
                isCardDetailsEditing={shouldDisplayPaymentEdit}
                setIsEditing={setIsUserEditingPayment}
                poNumber={poNumber}
                setPoNumber={setPoNumber}
                taxCode={taxCode}
                setTaxCode={setTaxCode}
                maskedCardNumber={user.customer?.maskedCardNumber}
                isPayByInvoiceCustomer={user.isPayByInvoiceCustomer}
              />
            ) : (
              <CardDetailsDisplay lastFourDigits={user.maskedCardNumber} />
            )
          }
          editTitle={
            user.hasCard() ? "Update payment details" : "Enter payment details"
          }
          editContent={
            <BillingPaymentSetup
              showCaptcha={false}
              buttonPlacement="right"
              showStripe={false}
              user={user}
              stripe={stripe}
              setHasUpdated={() => setIsUserEditingPayment(false)}
              addCardDetails={addCardDetails}
              showWarning={false}
              showTitle={false}
              inputVariant={InputVariantTypes.SNOWDROP}
              showCancel={user.isPBIEligible() && user.hasCard()}
              onCancel={() => setIsUserEditingPayment(false)}
              lowerContent={
                user.isPayByInvoiceCustomer
                  ? "This payment method will be saved for future card purchases. Your default payment method remains invoice. If you wish to change this please contact support."
                  : "This payment method will be saved for future card purchases."
              }
            />
          }
          displayLowerContent={
            paymentType === PAYMENT_TYPE.CARD
              ? "Payment will be made using this card"
              : "An invoice will be sent to your billing email address"
          }
        />
      </Container>
      <FormFooter
        showRecaptchaPolicy
        withBackground
        content={
          <>
            This page uses Stripe’s secure checkout. By using it you’re
            accepting their{" "}
            <Typography
              use="dashboardMicroLinkRegularInline"
              href="https://stripe.com/checkout/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Typography>{" "}
            and{" "}
            <Typography
              use="dashboardMicroLinkRegularInline"
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms & Conditions
            </Typography>
            .
          </>
        }
        rightContent={
          <a
            href="https://stripe.com"
            rel="noopener noreferrer"
            target="_blank"
            title="View the Stripe website (new tab)"
          >
            <img
              src="/images/app/stripe/powered-by-stripe-black.svg"
              alt="Powered by Stripe"
              width="150"
              height="34"
            />
          </a>
        }
      />
    </>
  );
};
